import { createContext, useContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { FormNode, FormEdge, Form } from '../utils/api/aboboApiTypes';
import { WizardPayload } from '../components/SharedTypes';
import { findNextNode, collectFormActions, buildResponsePayloads } from '../utils/formWizardUtils';
import { addFlowBreadcrumb } from '../utils/breadcrumbs';

export interface WizardFormData {
  [nodeId: string]: {
    data: string;
    customInput?: boolean;
  }[];
}

export interface WizardContextValue {
  // Form data
  form: UseFormReturn<WizardFormData>;
  nodes: FormNode[];
  edges: FormEdge[];
  
  // Attachments
  attachments: { [key: string]: string };
  setAttachments: (attachments: { [key: string]: string } | ((prev: { [key: string]: string }) => { [key: string]: string })) => void;
  
  // Navigation
  currentNode: FormNode | null;
  visitedNodeIds: string[];
  goToNextNode: () => void;
  goToPreviousNode: () => void;
  canGoBack: boolean;
  canMoveForward: boolean;
  isLastStep: boolean;
  
  // Actions
  handleNext: () => Promise<void>;
  formatWizardPayload: () => WizardPayload;
  
  // State
  responseState: {
    isSaving: boolean;
    isComplete: boolean;
  };
  
  // Props passed through
  responseId?: string;
}

interface WizardProviderProps {
  children: ReactNode;
  form: Form;
  onSave?: (payload: WizardPayload) => Promise<any> | any;
  onComplete: (payload: WizardPayload) => Promise<any> | any;
  onBack?: () => void;
  responseId?: string;
}

const FormWizardContext = createContext<WizardContextValue | null>(null);

export const useFormWizard = () => {
  const context = useContext(FormWizardContext);
  if (!context) {
    throw new Error('useFormWizard must be used within a FormWizardProvider');
  }
  return context;
};

// Context provider that owns all wizard business logic, state, and navigation
export const FormWizardProvider = ({
  children,
  form: formProp,
  onSave = async () => {},
  onComplete,
  onBack,
  responseId,
}: WizardProviderProps) => {
  const { i18n } = useTranslation();
  const [visitedNodeIds, setVisitedNodeIds] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<{ [key: string]: string }>({});
  const [responseState, setResponseState] = useState({
    isSaving: false,
    isComplete: false,
  });

  const nodes = useMemo(() => {
    return formProp?.lastPublishedRevision?.formNodes || [];
  }, [formProp]);

  const edges = useMemo(() => {
    return formProp?.lastPublishedRevision?.formEdges || [];
  }, [formProp]);

  const formMethods = useForm<WizardFormData>({
    mode: 'onChange',
    defaultValues: {},
  });

  // Watch form values to trigger re-renders when form changes
  const watchedValues = formMethods.watch();

  const currentNode = useMemo(() => {
    if (!nodes.length || !visitedNodeIds.length) return null;
    return nodes.find((node) => node.id === visitedNodeIds[visitedNodeIds.length - 1]) || null;
  }, [nodes, visitedNodeIds]);

  const isLastStep = useMemo(() => {
    if (!currentNode) return false;
    return findNextNode(currentNode, nodes, edges, watchedValues) === null;
  }, [currentNode, nodes, edges, watchedValues]);

  const canGoBack = useMemo(() => {
    return !!onBack || visitedNodeIds.length > 1;
  }, [onBack, visitedNodeIds.length]);

  const canMoveForward = useMemo(() => {
    if (!currentNode) return false;
    
    // Information nodes can always move forward
    if (currentNode.nodeType === 'information') return true;
    
    // Non-required fields can always move forward
    if (!currentNode.metadata?.required) return true;
    
    // For required fields, check if there's a valid value
    const currentValue = watchedValues[currentNode.id];
    
    // Check if there's a value with non-empty data
    return currentValue && 
           currentValue.length > 0 && 
           currentValue.some(v => {
             if (!v.data) return false;
             // Handle string data
             if (typeof v.data === 'string') {
               return v.data.trim().length > 0;
             }
             // Handle non-string data (like dates, numbers, etc.)
             return String(v.data).length > 0;
           });
  }, [currentNode, watchedValues]);

  const goToNextNode = useCallback(() => {
    if (!currentNode) return;
    
    const nextNode = findNextNode(currentNode, nodes, edges, formMethods.getValues());
    if (nextNode) {
      setVisitedNodeIds(prev => [...prev, nextNode.id]);
      // Initialize the form value for the next node
      formMethods.setValue(nextNode.id, []);
    }
  }, [currentNode, nodes, edges, formMethods]);

  const goToPreviousNode = useCallback(() => {
    if (visitedNodeIds.length <= 1 && onBack) {
      onBack();
      return;
    }

    if (visitedNodeIds.length > 1) {
      const newVisitedIds = [...visitedNodeIds];
      const previousNodeId = newVisitedIds.pop();
      setVisitedNodeIds(newVisitedIds);
      
      // Clear the form value for the previous node
      if (previousNodeId) {
        formMethods.setValue(previousNodeId, []);
      }
    }
  }, [visitedNodeIds, onBack, formMethods]);

  const nodesById = useMemo(() => {
    const map: { [id: string]: FormNode } = {};
    nodes.forEach(node => { map[node.id] = node; });
    return map;
  }, [nodes]);

  const formatWizardPayload = useCallback((): WizardPayload => {
    const formData = formMethods.getValues();
    const { triggeredActionKeys, annotationsByNodeId } = collectFormActions(nodes, edges, formData);
    const { responses, patientChartAttributes } = buildResponsePayloads(
      visitedNodeIds,
      nodesById,
      formData,
      annotationsByNodeId
    );

    return {
      attachments,
      responses,
      patientChartAttributes,
      language: i18n.language,
      triggeredActionKeys,
    };
  }, [formMethods, nodesById, attachments, visitedNodeIds, i18n.language, nodes, edges]);

  const handleNext = useCallback(async () => {
    if (!currentNode) return;

    const isValid = await formMethods.trigger(currentNode.id);
    if (!isValid) {
      addFlowBreadcrumb('Form validation blocked advance', {
        nodeId: currentNode.id,
        nodeSubtype: currentNode.nodeSubtype,
        stepIndex: visitedNodeIds.length,
      });
      return;
    }

    const payload = formatWizardPayload();
    setResponseState(prev => ({ ...prev, isSaving: true }));

    try {
      if (isLastStep) {
        if (currentNode.metadata?.exitPoint) {
          addFlowBreadcrumb('Form exit point reached (screen-out)', {
            nodeId: currentNode.id,
            nodeSubtype: currentNode.nodeSubtype,
          });
          window.location.replace('https://google.com');
          return;
        }

        addFlowBreadcrumb('Form completed', {
          nodeId: currentNode.id,
          stepCount: visitedNodeIds.length,
        });
        await onComplete(payload);
        setResponseState({ isSaving: false, isComplete: true });
      } else {
        addFlowBreadcrumb('Form advanced to next question', {
          fromNodeId: currentNode.id,
          fromNodeSubtype: currentNode.nodeSubtype,
          stepIndex: visitedNodeIds.length,
        });
        await onSave(payload);
        setResponseState(prev => ({ ...prev, isSaving: false }));
        goToNextNode();
      }
    } catch (error) {
      setResponseState(prev => ({ ...prev, isSaving: false }));
      console.error('Error handling next step:', error);
      // Previously only console.error'd — this save/complete failure was invisible
      // in Sentry. Capture it so the flow breadcrumbs above have an event to attach to.
      Sentry.captureException(error, {
        tags: { form_wizard_error: true },
        extra: {
          nodeId: currentNode.id,
          nodeSubtype: currentNode.nodeSubtype,
          isLastStep,
        },
      });
    }
  }, [currentNode, formMethods, formatWizardPayload, isLastStep, onComplete, onSave, goToNextNode, visitedNodeIds.length]);

  // Initialize the first node
  useEffect(() => {
    if (nodes.length > 0 && visitedNodeIds.length === 0) {
      try {
        const firstNode = findNextNode(null, nodes, edges, {});
        if (firstNode) {
          setVisitedNodeIds([firstNode.id]);
          formMethods.setValue(firstNode.id, []);
        } else {
          // No first node found - form structure is invalid
          const error = new Error('Unable to find first node in form');
          Sentry.captureException(error, {
            tags: {
              form_initialization_error: true,
            },
            extra: {
              formId: formProp.id,
              nodesCount: nodes.length,
              edgesCount: edges.length,
            },
          });
        }
      } catch (error) {
        // Catch any errors during initialization
        Sentry.captureException(error, {
          tags: {
            form_initialization_error: true,
          },
          extra: {
            formId: formProp.id,
            nodesCount: nodes.length,
            edgesCount: edges.length,
          },
        });
      }
    }
  }, [nodes, edges, visitedNodeIds.length, formMethods, formProp]);

  const contextValue: WizardContextValue = {
    form: formMethods,
    nodes,
    edges,
    attachments,
    setAttachments,
    currentNode,
    visitedNodeIds,
    goToNextNode,
    goToPreviousNode,
    canGoBack,
    canMoveForward,
    isLastStep,
    handleNext,
    formatWizardPayload,
    responseState,
    responseId,
  };

  return <FormWizardContext.Provider value={contextValue}>{children}</FormWizardContext.Provider>;
};
